import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
} from "recharts";
import { Shield, TrendingDown } from "lucide-react";

const fmtMoney = (v) => `$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (v) => (v || 0).toLocaleString("en-US");
const fmtDate = (s) => {
    if (!s) return "";
    try {
        const d = new Date(s);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch (e) { void e; return s; }
};

// Custom tooltip — labels every series clearly and shows the derived CTR + margin amount
// in one compact block. No duplicate "Margin" entries; the chart only renders one margin series
// (the % line) — the absolute margin amount appears as a contextual row inside the tooltip.
function CustomTooltip({ active, payload, label }) {
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0]?.payload || {};
    const ctr = row.impressions > 0 ? (row.clicks / row.impressions) * 100 : 0;
    const marginPositive = (row.margin || 0) >= 0;
    return (
        <div className="bg-white border border-zinc-200 rounded-md shadow-md text-xs px-3 py-2 min-w-[200px]" data-testid="margin-trend-tooltip">
            <div className="font-medium text-zinc-900 mb-2 pb-1.5 border-b border-zinc-100">{fmtDate(label)}</div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {/* Money block */}
                <div className="col-span-2 text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">Money</div>
                <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm" style={{ background: "var(--wl-primary-hex)" }} />
                    <span className="text-zinc-500">Campaign Budget</span>
                </div>
                <div className="text-right tabular-nums text-zinc-900">{fmtMoney(row.display_spend)}</div>

                <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm bg-zinc-600" />
                    <span className="text-zinc-500">Campaign Spends</span>
                </div>
                <div className="text-right tabular-nums text-zinc-900">{fmtMoney(row.actual_spend)}</div>

                <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <span className={marginPositive ? "text-amber-500" : "text-red-500"}>Margin</span>
                </div>
                <div className={`text-right tabular-nums font-medium ${marginPositive ? "text-amber-500" : "text-red-500"}`}>
                    {fmtMoney(row.margin)}
                    <span className="ml-1 text-[10px] opacity-70">({(row.margin_pct || 0).toFixed(1)}%)</span>
                </div>

                {/* Volume block */}
                <div className="col-span-2 text-[10px] uppercase tracking-wider text-zinc-400 mt-1.5">Volume</div>
                <div className="text-zinc-500">Impressions</div>
                <div className="text-right tabular-nums text-zinc-900">{fmtNum(row.impressions)}</div>
                <div className="text-zinc-500">Clicks</div>
                <div className="text-right tabular-nums text-zinc-900">{fmtNum(row.clicks)}</div>
                <div className="text-zinc-500">CTR</div>
                <div className="text-right tabular-nums text-zinc-900">{ctr.toFixed(2)}%</div>
            </div>
        </div>
    );
}

// Inline legend chips — replaces Recharts' default Legend so the styling matches the rest of the dashboard
function LegendChips() {
    const items = [
        { label: "Campaign Budget", color: "var(--wl-primary-hex)", shape: "square" },
        { label: "Campaign Spends", color: "#52525b", shape: "square" },
        { label: "Margin %", color: "#f59e0b", shape: "line" },
    ];
    return (
        <div className="flex items-center justify-center gap-5 text-[11px] text-zinc-500 pt-1.5 pb-1">
            {items.map((it) => (
                <div key={it.label} className="inline-flex items-center gap-1.5">
                    {it.shape === "line" ? (
                        <span className="h-0.5 w-4 rounded-full" style={{ background: it.color }} />
                    ) : (
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: it.color, opacity: 0.65 }} />
                    )}
                    {it.label}
                </div>
            ))}
        </div>
    );
}

export default function MarginTrendChart({ filter, threshold = 30 }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ignore = false;
        const load = async () => {
            setLoading(true);
            try {
                const params = { ...filter };
                Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
                const { data } = await api.get("/reports/margin-trend", { params });
                if (!ignore) setData(data);
            } catch (e) {
                void e;
                if (!ignore) setData([]);
            } finally {
                if (!ignore) setLoading(false);
            }
        };
        load();
        return () => { ignore = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filter)]);

    const totals = useMemo(() => {
        const display = data.reduce((a, r) => a + (r.display_spend || 0), 0);
        const actual = data.reduce((a, r) => a + (r.actual_spend || 0), 0);
        const margin = display - actual;
        const pct = display > 0 ? (margin / display) * 100 : 0;
        return { display, actual, margin, pct };
    }, [data]);
    const belowFloor = data.filter((d) => (d.margin_pct || 0) < threshold).length;

    // Symmetric Y-axis range for margin % so the floor reference line is always visible
    const minPct = Math.min(0, ...data.map((d) => d.margin_pct || 0));
    const maxPct = Math.max(100, ...data.map((d) => d.margin_pct || 0));
    const pctDomain = [Math.floor(minPct - 5), Math.ceil(maxPct + 5)];

    return (
        <div className="bg-white border border-zinc-200 mb-6" data-testid="margin-trend-card">
            <div className="flex flex-wrap items-center justify-between border-b border-zinc-200 px-5 py-3 gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-8 w-8 flex items-center justify-center rounded-md bg-amber-500/10 border border-amber-500/20">
                        <Shield className="h-4 w-4 text-amber-500" />
                    </div>
                    <div>
                        <div className="overline">Internal · daily trend</div>
                        <h3 className="font-display text-base text-zinc-900">Campaign Budget vs Campaign Spends · Margin %</h3>
                    </div>
                </div>
                <div className="flex items-center gap-6 text-xs">
                    <div className="text-right">
                        <div className="overline">Total margin</div>
                        <div className={`font-display text-lg tabular-nums ${totals.margin < 0 ? "text-red-500" : "text-amber-500"}`}>{fmtMoney(totals.margin)}</div>
                    </div>
                    <div className="text-right">
                        <div className="overline">Window avg %</div>
                        <div className={`font-display text-lg tabular-nums ${totals.pct < threshold ? "text-red-500" : "text-amber-500"}`}>{totals.pct.toFixed(1)}%</div>
                    </div>
                    {belowFloor > 0 && (
                        <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 text-red-500 border border-red-500/20" data-testid="margin-trend-floor-warning">
                            <TrendingDown className="h-3 w-3" />
                            {belowFloor} day(s) below {threshold}% floor
                        </div>
                    )}
                </div>
            </div>
            <div className="p-3">
                <div style={{ width: "100%", height: 240 }}>
                    {!loading && data.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-zinc-500 text-sm">No margin data in this window.</div>
                    ) : (
                        <ResponsiveContainer minWidth={0} minHeight={220}>
                        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} barGap={3}>
                            <CartesianGrid stroke="#e4e4e7" vertical={false} strokeDasharray="2 4" />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: "#52525b", fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={fmtDate}
                            />
                            <YAxis
                                yAxisId="money"
                                tick={{ fill: "#52525b", fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                            />
                            <YAxis
                                yAxisId="pct"
                                orientation="right"
                                domain={pctDomain}
                                tick={{ fill: "#52525b", fontSize: 11 }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                            <ReferenceLine yAxisId="pct" y={threshold} stroke="#ef4444" strokeDasharray="3 3" label={{ value: `${threshold}% floor`, fill: "#ef4444", fontSize: 10, position: "insideTopRight" }} />
                            <ReferenceLine yAxisId="pct" y={0} stroke="#a1a1aa" strokeOpacity={0.5} />

                            {/* Side-by-side bars — campaign budget vs campaign spends */}
                            <Bar yAxisId="money" dataKey="display_spend" name="Campaign Budget" fill="var(--wl-primary-hex)" opacity={0.7} radius={[3, 3, 0, 0]} maxBarSize={28} />
                            <Bar yAxisId="money" dataKey="actual_spend" name="Campaign Spends" fill="#52525b" opacity={0.65} radius={[3, 3, 0, 0]} maxBarSize={28} />

                            {/* Single margin series — the % line on the right axis. The absolute margin amount
                                appears inside the tooltip so we don't have two visually-identical margin series. */}
                            <Line yAxisId="pct" type="monotone" dataKey="margin_pct" name="Margin %" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 2.5, fill: "#f59e0b" }} activeDot={{ r: 5 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
                </div>
                <LegendChips />
            </div>
        </div>
    );
}
